export type PageType = 'home' | 'seo_landing' | 'pricing' | 'policy' | 'contact';

export type RouteConfig = {
  path: string;
  title: string;
  navLabel?: string;
  description: string;
  pageType: PageType;
  h1: string;
  eyebrow: string;
  cta: string;
  focus: string;
  bullets?: string[];
  noIndex?: boolean;
};

export const siteUrl = (import.meta.env.VITE_SITE_URL || 'https://ai-anime-generator.pages.dev').replace(/\/$/, '');

export const routeConfigs: RouteConfig[] = [
  {
    path: '/',
    title: 'AI Anime Generator — Turn Photos Into Anime Avatars',
    navLabel: 'Create',
    description:
      'Upload an authorized photo or describe an original character, choose a safe anime style, and try one anime-style image free each day before sign-up.',
    pageType: 'home',
    h1: 'Turn Your Photo Into an Anime Avatar',
    eyebrow: 'Daily limited free trial · 1 image before sign-up',
    cta: 'Generate My Anime Avatar — Free Trial',
    focus: 'Photo-first anime avatar generation with safe generic style presets and transparent credit rules.',
  },
  {
    path: '/ai-anime-generator-from-photo',
    title: 'AI Anime Generator from Photo — Safe Anime Avatar Tool',
    description:
      'Upload a photo you own or have permission to use and try a safe anime avatar generation flow with one daily limited free image.',
    pageType: 'seo_landing',
    h1: 'AI Anime Generator from Photo',
    eyebrow: 'Photo to safe anime avatar',
    cta: 'Upload My Photo — Free Trial',
    focus: 'Use authorized photos, pick a generic anime-inspired preset, and preview how credits unlock more images and higher quality.',
    bullets: ['Best for selfies, pets, couples, and PFP photos.', 'Clear permission reminder before generation.', 'Mock/sample before-after cards are labeled.'],
  },
  {
    path: '/photo-to-anime-converter',
    title: 'Photo to Anime Converter — Try One Image Free Each Day',
    description:
      'Convert a clear authorized photo into a generic anime-inspired avatar with a daily limited free trial and visible safety rules.',
    pageType: 'seo_landing',
    h1: 'Photo to Anime Converter',
    eyebrow: 'Upload · style · generate',
    cta: 'Convert My Photo — Free Trial',
    focus: 'A conversion-focused page for users who want a simple photo-to-anime flow without implying unrestricted free use.',
    bullets: ['Use JPG, PNG, or WebP photos.', 'Free capacity may queue or require sign-in.', 'Credits unlock HD and watermark-free options.'],
  },
  {
    path: '/free-ai-anime-generator-no-sign-up',
    title: 'Free AI Anime Generator No Sign Up — Daily Limited Trial',
    description:
      'Try one safe anime-style image free each day before sign-up. More images, premium quality, HD downloads, and watermark-free output use credits.',
    pageType: 'seo_landing',
    h1: 'Free AI Anime Generator No Sign Up',
    eyebrow: 'Honest daily limited trial',
    cta: 'Generate 1 Free Anime Image',
    focus: 'No sign-up is needed for the daily limited free trial; credit and capacity limits are shown before premium actions.',
    bullets: ['Anonymous output is locked to 1 image/day.', 'Queue or capacity limits may apply.', 'No unrestricted generation promise.'],
  },
  {
    path: '/ai-anime-character-generator',
    title: 'AI Anime Character Generator — Original Anime OC Ideas',
    description:
      'Describe an original character or add an authorized photo, then choose a safe generic anime-inspired style for avatar-ready results.',
    pageType: 'seo_landing',
    h1: 'AI Anime Character Generator',
    eyebrow: 'Original characters only',
    cta: 'Create My Anime Character',
    focus: 'Designed for original characters and avatar concepts, not exact copies of existing anime characters, living artists, or celebrities.',
    bullets: ['Prompt-only and photo-plus-prompt modes.', 'Avoid public figures and copyrighted characters.', 'Use credits for 4-image variations.'],
  },
  {
    path: '/pricing',
    title: 'Pricing and Credits — AI Anime Generator',
    navLabel: 'Pricing',
    description:
      'See the daily free trial, starter credit pack, creator packs, and rules for premium quality, HD downloads, and watermark-free output.',
    pageType: 'pricing',
    h1: 'Pricing and Credits',
    eyebrow: 'Free entry, credits for more control',
    cta: 'Generate 1 Free Image',
    focus: 'Transparent credit rules: anonymous daily trial for one standard image, then credits for more control.',
  },
  {
    path: '/privacy',
    title: 'Privacy Policy — AI Anime Generator',
    description: 'Privacy policy draft for AI Anime Generator, including upload, account, provider, analytics, and user control disclosures.',
    pageType: 'policy',
    h1: 'Privacy Policy',
    eyebrow: 'Policy draft',
    cta: 'Manage Cookie Settings',
    focus: 'We do not intentionally send uploaded images, generated images, full prompts, payment card data, or sensitive identity information to analytics tools.',
  },
  {
    path: '/terms',
    title: 'Terms of Use — AI Anime Generator',
    description: 'Terms of use draft covering acceptable use, credits, provider availability, uploads, and safe anime-style generation limits.',
    pageType: 'policy',
    h1: 'Terms of Use',
    eyebrow: 'Policy draft',
    cta: 'Start a Safe Request',
    focus: 'Use the service only with photos and prompts you have rights to provide; credits and provider availability are subject to final launch terms.',
  },
  {
    path: '/ai-content-policy',
    title: 'AI Content Policy — AI Anime Generator',
    description: 'AI content policy for safe generic anime-inspired generation, restricted requests, moderation refusals, and reporting.',
    pageType: 'policy',
    h1: 'AI Content Policy',
    eyebrow: 'Safety rules',
    cta: 'Review Safety Rules',
    focus: 'Do not request celebrity impersonation, copyrighted character recreation, adult content, or non-consensual likeness edits.',
  },
  {
    path: '/cookie-policy',
    title: 'Cookie and Analytics Notice — AI Anime Generator',
    description: 'Cookie and analytics notice with consent controls for GA4, Search Console verification, and privacy-safe event measurement.',
    pageType: 'policy',
    h1: 'Cookie and Analytics Notice',
    eyebrow: 'Consent-aware analytics',
    cta: 'Cookie Settings',
    focus: 'GA4 loads only when configured and allowed by consent; rejecting analytics does not block the core generation flow.',
  },
  {
    path: '/dmca',
    title: 'DMCA and Takedown — AI Anime Generator',
    description: 'DMCA/takedown draft for reporting copyright, likeness, privacy, or safety concerns related to uploads and generated results.',
    pageType: 'policy',
    h1: 'DMCA and Takedown',
    eyebrow: 'Rights and safety reports',
    cta: 'Report a Rights Issue',
    focus: 'Use the contact path to report copyright, likeness, privacy, safety, or abuse concerns; do not include unnecessary sensitive details in analytics.',
  },
  {
    path: '/contact',
    title: 'Contact and Support — AI Anime Generator',
    navLabel: 'Contact',
    description: 'Contact AI Anime Generator support for product, privacy, takedown, credits, or provider disclosure questions.',
    pageType: 'contact',
    h1: 'Contact and Support',
    eyebrow: 'Support draft',
    cta: 'Contact Support',
    focus: 'Support email, legal entity, jurisdiction, and retention periods are launch-blocking placeholders for the operator to confirm.',
  },
];

export const getRouteConfig = (pathname: string): RouteConfig => {
  const normalized = pathname !== '/' ? pathname.replace(/\/$/, '') : '/';
  return routeConfigs.find((route) => route.path === normalized) ?? routeConfigs[0];
};
